import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, UserRound, ArrowRight } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Logo } from "./Logo";
import { NAV_LINKS } from "@/data/nav";
import { cn } from "@/lib/utils";

const linkCls = ({ isActive }) =>
  cn(
    "text-sm font-medium transition-colors duration-200 hover:text-[#F7F5F0]",
    isActive ? "text-[#FF6A00]" : "text-[#F7F5F0]/70",
  );

const MobileMenu = () => {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button className="lg:hidden grid h-11 w-11 place-items-center rounded-full border border-[#2E2E2E]" aria-label="Open menu" data-testid="mobile-menu-button">
          <Menu className="h-5 w-5" />
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-sm border-l-[#2E2E2E] bg-[#080808] p-0" data-testid="mobile-menu">
        <SheetTitle className="sr-only">Menu</SheetTitle>
        <div className="flex h-full flex-col px-6 pt-6 pb-8">
          <Logo onClick={close} />
          <nav className="mt-10 flex flex-col" aria-label="Mobile">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={close}
                data-testid={`mobile-nav-${l.to.slice(1)}`}
                className={({ isActive }) =>
                  cn("flex items-center justify-between border-b border-[#1c1c1c] py-4 font-display text-2xl font-semibold", isActive ? "text-[#FF6A00]" : "text-[#F7F5F0]")
                }
              >
                {l.label}
                <ArrowRight className="h-5 w-5 opacity-40" />
              </NavLink>
            ))}
          </nav>
          <div className="mt-auto grid gap-3 pt-8">
            <Link to="/plans" onClick={close} className="lc-btn-primary w-full" data-testid="mobile-menu-join">Join WeeLogRun</Link>
            <Link to="/account" onClick={close} className="lc-btn-ghost w-full" data-testid="mobile-menu-account">My WeeLogRun</Link>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [pathname]);

  return (
    <header
      data-testid="site-header"
      className={cn(
        "fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-300 border-b",
        scrolled || pathname !== "/" ? "bg-[#080808]/90 backdrop-blur-xl border-[#232323]" : "bg-transparent border-transparent",
      )}
    >
      <div className="lc-container flex h-20 items-center justify-between gap-6">
        <Logo />
        <nav className="hidden lg:flex items-center gap-8" aria-label="Main">
          {NAV_LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} className={linkCls} data-testid={`nav-${l.to.slice(1)}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          <Link to="/account" className="hidden sm:inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium text-[#F7F5F0]/80 hover:text-[#F7F5F0]" data-testid="nav-account">
            <UserRound className="h-4 w-4" /> My WeeLogRun
          </Link>
          <Link to="/plans" className="lc-btn-primary hidden sm:inline-flex !px-5 !py-2.5" data-testid="nav-join-button">Join</Link>
          <MobileMenu />
        </div>
      </div>
    </header>
  );
};
